// src/pages/AutoEvaluationPage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOrdersForAutoEvaluation } from '../services/orderService';
import { autoEvaluateOrder } from '../services/evaluationService';

import Sidebar from '../components/Sidebar';

const AutoEvaluationPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');

  const sortOrdersBySubmittedAt = (orders, order = 'desc') => {
    return [...orders].sort((a, b) => {
      const aDate = new Date(a.submitted_at || 0);
      const bDate = new Date(b.submitted_at || 0);
      return order === 'asc' ? aDate - bDate : bDate - aDate;
    });
  };

  const toggleSortOrder = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    setOrders(prev => sortOrdersBySubmittedAt(prev, newOrder));
  };

  const handleFetchOrders = async () => {
    setLoading(true);
    try {
      const data = await fetchOrdersForAutoEvaluation();
      if (Array.isArray(data)) {
        const sorted = sortOrdersBySubmittedAt(data, sortOrder);
        setOrders(sorted);
        setShowTable(true);
      } else {
        console.error('Unexpected data type from fetchOrdersForAutoEvaluation');
      }
    } catch (err) {
      console.error('Error fetching orders for auto evaluation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders((prevSelected) =>
      prevSelected.includes(orderId)
        ? prevSelected.filter((id) => id !== orderId)
        : [...prevSelected, orderId]
    );
  };

  const handleAutoEvaluate = async () => {
    if (selectedOrders.length === 0) {
      console.log("No orders selected for evaluation.");
      return;
    }

    console.log("Starting auto evaluation for orders:", selectedOrders);
    setEvaluating(true);
    try {
      for (const orderId of selectedOrders) {
        console.log(`Evaluating order: ${orderId}`);
        const evaluationData = {};
        const result = await autoEvaluateOrder(orderId, evaluationData);
        console.log(`Order ${orderId} evaluation result:`, result);
      }

      alert(`Auto evaluation completed for ${selectedOrders.length} order(s).`);
      setSelectedOrders([]);
      await handleFetchOrders(); // Refresh list
    } catch (error) {
      console.error("Auto evaluation error:", error);
      alert("Auto evaluation failed for one or more orders. Check console for details.");
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <h1 className="text-2xl font-bold mb-1">Auto Evaluation</h1>
        <p className="text-gray-600 mb-3">
          Orders whose <strong>order_status</strong> is "<strong>Under Review</strong>" and <strong>evaluation_status</strong> is "<strong>not_evaluated</strong>" will appear here.
        </p>

        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mb-4"
          onClick={handleFetchOrders}
          disabled={loading}
        >
          {loading ? 'Fetching Orders...' : 'Fetch Orders for Auto Evaluation'}
        </button>

        {showTable && (
          <>
            <p className="mt-4 mb-2 text-gray-600">Showing {orders.length} orders</p>
            <div className="overflow-x-auto">
              <table className="min-w-full border text-sm bg-white shadow-sm rounded-lg">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-center"><span className="sr-only">Select</span></th>
                    <th className="border p-2 text-left">Order ID</th>
                    <th className="border p-2 text-left">Freelancer ID</th>
                    <th className="border p-2 text-left">Freelancer Name</th>
                    <th className="border p-2 text-left">Task ID</th>
                    <th className="border p-2 text-left">Status</th>
                    <th className="border p-2 text-left">Assigned At</th>
                    <th className="border p-2 text-left">Due Date</th>
                    <th className="border p-2 text-left cursor-pointer select-none" onClick={toggleSortOrder}>
                      Submitted At <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    </th>
                    <th className="border p-2 text-left">Payout Max</th>
                    <th className="border p-2 text-left">Payout Eligible</th>
                    <th className="border p-2 text-left">Payout Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.order_id} className="hover:bg-gray-50">
                      <td className="border p-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.order_id)}
                          onChange={() => handleCheckboxChange(order.order_id)}
                        />
                      </td>
                      <td className="border p-2">{order.order_id}</td>
                      <td className="border p-2">{order.freelancer_id ?? '—'}</td>
                      <td className="border p-2">{order.freelancer_name ?? '—'}</td>
                      <td className="border p-2">{order.task_id ?? '—'}</td>
                      <td className="border p-2">{order.order_status}</td>
                      <td className="border p-2">
                        {order.assigned_at ? new Date(order.assigned_at).toLocaleString() : '—'}
                      </td>
                      <td className="border p-2">
                        {order.due_date ? new Date(order.due_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="border p-2">
                        {order.submitted_at ? new Date(order.submitted_at).toLocaleString() : '—'}
                      </td>
                      <td className="border p-2">
                        {order.max_payout ? `₹ ${order.max_payout.toLocaleString()}` : '—'}
                      </td>
                      <td className="border p-2">{order.payout_eligible_amount ?? '—'}</td>
                      <td className="border p-2">{order.payout_status ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4">
              <button
                className={`px-4 py-2 rounded text-white ${
                  selectedOrders.length > 0 && !evaluating
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                disabled={selectedOrders.length === 0 || evaluating}
                onClick={handleAutoEvaluate}
              >
                {evaluating ? 'Auto evaluating...' : 'Auto evaluate the selected orders'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AutoEvaluationPage;
